(function () {
  'use strict';

  if (customElements.get('social-videos')) return;

  customElements.define(
    'social-videos',
    class SocialVideos extends HTMLElement {
      static allSections = [];

      constructor() {
        super();
        this.resizeHandler = this.onResize.bind(this);
        this.observer = null;
      }

      isMobile() {
        return (
          window.matchMedia('(max-width: 767px)').matches ||
          !window.matchMedia('(hover: hover) and (pointer: fine)').matches ||
          (window.theme && window.theme.isMobile) ||
          false
        );
      }

      isDesktopHover() {
        return (
          window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
          window.innerWidth >= 768
        );
      }

      connectedCallback() {
        SocialVideos.allSections.push(this);
        this.videoItems = this.querySelectorAll('[data-social-video-item]');

        this.videoItems?.forEach((item, index) => {
          this.initVideoItem(item, index);
        });

        this.handleInitialAutoplay();

        window.addEventListener('resize', this.resizeHandler);
        document.addEventListener('theme:resize:width', this.resizeHandler);

        this.initIntersectionObserver();
      }

      onResize() {
        this.handleInitialAutoplay();
      }

      initIntersectionObserver() {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const firstItem = this.videoItems?.[0];
              if (!firstItem || !firstItem._video) return;

              // Only manage first video if in initial muted autoplay state
              if (firstItem.classList.contains('is-muted-autoplay')) {
                if (entry.isIntersecting) {
                  if (firstItem._video.paused) {
                    firstItem._video.play().catch(() => {});
                  }
                } else {
                  if (!firstItem._video.paused) {
                    firstItem._video.pause();
                  }
                }
              }
            });
          },
          { threshold: 0.25 }
        );

        this.observer.observe(this);
      }

      handleInitialAutoplay() {
        if (!this.videoItems || this.videoItems.length === 0) return;

        const firstItem = this.videoItems[0];
        const video = firstItem.querySelector('video');
        if (!video) return;

        // If not already explicitly played with sound
        if (!firstItem.classList.contains('is-playing')) {
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');

          firstItem.classList.add('is-muted-autoplay');

          const startAutoplay = () => {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch((err) => {
                console.log('Initial muted autoplay prevented:', err);
              });
            }
          };

          if (video.readyState >= 2) {
            startAutoplay();
          } else {
            video.addEventListener('loadeddata', startAutoplay, { once: true });
            startAutoplay();
          }
        }
      }

      initVideoItem(item, index) {
        const video = item.querySelector('video');
        const playButton = item.querySelector('[data-video-play]');

        if (!video) return;

        item._video = video;
        item._playButton = playButton;
        item._index = index;

        let isDragging = false;
        let startX = 0;
        let startY = 0;

        item.addEventListener('pointerdown', (evt) => {
          startX = evt.clientX;
          startY = evt.clientY;
          isDragging = false;
        });

        item.addEventListener('pointermove', (evt) => {
          if (Math.abs(evt.clientX - startX) > 10 || Math.abs(evt.clientY - startY) > 10) {
            isDragging = true;
          }
        });

        // Click anywhere on the video item to play/pause
        item.addEventListener('click', (evt) => {
          if (isDragging) return;
          if (item.closest('grid-slider')?.classList.contains('is-dragging')) return;
          if (item.closest('.flickity-enabled')?.classList.contains('is-pointer-down')) return;

          evt.preventDefault();
          this.handleButtonClick(item);
        });

        // Keyboard accessibility
        item.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter' || evt.key === ' ') {
            if (evt.target === playButton || evt.target === item) {
              evt.preventDefault();
              this.handleButtonClick(item);
            }
          }
        });
      }

      handleButtonClick(item) {
        if (!item._video) return;

        // Case 1: First video currently in muted autoplay on mobile
        // Clicking play button activates sound and switches to pause button
        if (item.classList.contains('is-muted-autoplay')) {
          this.playVideoWithSound(item);
          return;
        }

        // Case 2: Currently playing with sound -> pause it
        if (item.classList.contains('is-playing')) {
          this.pauseVideo(item);
          return;
        }

        // Case 3: Paused or idle -> play with sound
        this.playVideoWithSound(item);
      }

      playVideoWithSound(item) {
        if (!item._video) return;

        this.pauseAllOtherVideos(item);

        item._video.muted = false;
        item._video.loop = true;

        const playPromise = item._video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              item.classList.remove('is-muted-autoplay');
              item.classList.add('is-playing');
              if (item._playButton) {
                item._playButton.setAttribute(
                  'aria-label',
                  window.theme?.strings?.pauseVideo || 'Pause video'
                );
              }
            })
            .catch((err) => {
              console.log('Unmuted playback prevented, falling back to muted:', err);
              // Fallback to muted playback if browser restricts sound prior to user interaction
              item._video.muted = true;
              item._video.play().then(() => {
                item.classList.remove('is-muted-autoplay');
                item.classList.add('is-playing');
              }).catch((e) => console.log('Video play prevented:', e));
            });
        }
      }

      pauseVideo(item) {
        if (!item._video) return;

        item.classList.remove('is-playing');
        item.classList.remove('is-muted-autoplay');
        item._video.pause();
        item._video.muted = true;

        if (item._playButton) {
          item._playButton.setAttribute(
            'aria-label',
            window.theme?.strings?.playVideo || 'Play video'
          );
        }
      }

      pauseAllOtherVideos(currentItem) {
        SocialVideos.allSections.forEach((section) => {
          section.videoItems?.forEach((item) => {
            if (item !== currentItem && item._video && !item._video.paused) {
              this.pauseVideo(item);
            }
          });
        });
      }

      disconnectedCallback() {
        const idx = SocialVideos.allSections.indexOf(this);
        if (idx > -1) SocialVideos.allSections.splice(idx, 1);

        window.removeEventListener('resize', this.resizeHandler);
        document.removeEventListener('theme:resize:width', this.resizeHandler);

        if (this.observer) {
          this.observer.disconnect();
        }
      }
    }
  );
})();
